import { useRouter } from 'next/navigation'
import { useLocale } from 'next-intl'
import { searchAirports } from '@/src/api/requests/search-airports'
import { getFormattedDate } from '@/src/utils/date'
import dayjs from 'dayjs'

/**
 * Handles Book Now button click for Top Cities section
 * Auto-fills origin/destination from city names, sets check-in date to 7 days from today,
 * and redirects to SRP with the generated search URL
 */
export const useHandleTopCityBookNow = () => {
    const router = useRouter()
    const locale = useLocale()

    const handleBookNow = async (originCity: string, destinationCity: string) => {
        try {
            // Search for airport codes for both cities
            const [originResults, destinationResults] = await Promise.all([
                searchAirports(originCity),
                searchAirports(destinationCity)
            ])

            // Get the first (most relevant) airport code from results
            const originAirportCode =
                (originResults as any)?.data?.[0]?.payload?.airportCode || originCity.toUpperCase().slice(0, 3)
            const destinationAirportCode =
                (destinationResults as any)?.data?.[0]?.payload?.airportCode ||
                destinationCity.toUpperCase().slice(0, 3)

            // Get airport display names
            const originDisplayName = (originResults as any)?.data?.[0]?.text || originCity
            const destinationDisplayName = (destinationResults as any)?.data?.[0]?.text || destinationCity

            // Calculate date 7 days from today
            const departDate = dayjs().add(7, 'day')
            const formattedDepartDate = getFormattedDate(departDate.toDate(), 'DD/MM/YYYY')

            // Build query parameters
            const queryParams = new URLSearchParams()
            queryParams.set('from', originAirportCode)
            queryParams.set('to', destinationAirportCode)
            queryParams.set('origin', originDisplayName)
            queryParams.set('destination', destinationDisplayName)
            queryParams.set('adults', '1')
            queryParams.set('childs', '0')
            queryParams.set('infants', '0')
            queryParams.set('class', 'Economy')
            queryParams.set('depart_date', formattedDepartDate)
            queryParams.set('return_date', '') // One-way trip

            // Generate SRP URL
            const url = `/${locale}/flights/results?${queryParams.toString()}`

            // Navigate to SRP
            router.push(url)
        } catch (error) {
            console.error('Error handling Book Now click:', error)
            // Fallback: navigate to flights page if airport search fails
            router.push(`/${locale}/flights`)
        }
    }

    return { handleBookNow }
}
